import { Button, Col, Form, Input, message, Progress, Row } from "antd";
import { Formik, FormikProps } from "formik";
import { useRef, useState } from "react";
import * as Yup from "yup";
import zxcvbn from 'zxcvbn';

type Props = {
    checkTokenStatus: () => Promise<boolean>
    resetPassword: (newPassword: string) => Promise<{ success: boolean, error?: string }>
    setKey: React.Dispatch<React.SetStateAction<string>>
}

const PasswordResetForm: React.FC<Props> = ({ checkTokenStatus, resetPassword, setKey }) => {
    const formRef = useRef<FormikProps<any>>();
    const [canSubmit, setCanSubmit] = useState<boolean>(false);

    const initialValues = {
        newPassword: "",
        confirmNewPassword: ""
    }

    /**
     * Get password strength score using zxcvbn
     * @returns 
     */
    const getPasswordStrength = () => {
        const passwordInput = formRef.current?.values.newPassword;

        if (passwordInput?.length > 0) {
            const strength = zxcvbn(passwordInput);
            return strength?.score;
        }

        return -1;
    }

    /**
     * Get password strength bar color based on password strength
     * @returns 
     */
    const getStrengthColor = () => {
        switch (getPasswordStrength()) {
            case 0:
            case 1:
            case 2:
                return "#d9534f";
            case 3:
                return "#f2cb41";
            case 4:
                return "#1DBAB5";
            default:
                /** do nothing */
                break;
        }
    }

    /**
     * Handle submit password reset form
     * @param values 
     */
    const handleSubmit = async (values) => {
        if (await checkTokenStatus()) {
            const result = await resetPassword(values.newPassword)

            if (result.success) {
                setKey("success")
            } else {
                message.error(result.error || "Error resetting password", 6);
            }
        }
    }

    return <>
        <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                newPassword: Yup.string().required("Please enter your new password.").test('specialCharacters', "Password contains invalid special character(s).", val =>
                    !!val && !(/[^a-zA-Z0-9!@#$%^*\-_+=]/).test(val)
                )
                .min(10, "Password should at least be 10 characters long.")
                .max(128, "Password must be 128 characters or fewer.")
                .matches(/[A-Z]/, "Password should have at least 1 upper-case letter.")
                .matches(/[a-z]/, "Password should have at least 1 lower-case letter.")
                .matches(/[0-9]/, "Password should have at least 1 number.")
                .matches(/[!@#$%^*\-_+=]/, "Password should have at least 1 special character."),
                confirmNewPassword: Yup.string().required("Please confirm new password.").test('confirmPasswordMatch', 'Confirm password does not match.', (val, otherProps) =>
                    otherProps.parent.newPassword === val
                ),
            })}
            onSubmit={handleSubmit}
            validateOnMount
        >
            {({
                values,
                errors,
                touched,
                dirty,
                handleSubmit,
                handleChange,
                handleBlur,
                setFieldValue,
                setFieldTouched,
            }) => {
                setCanSubmit(Object.keys(errors).length === 0 && dirty);

                return (
                    <Form id="PasswordResetForm" onFinish={handleSubmit}>
                        <Row justify="center" gutter={[32, 16]}>
                            <Col span={10}>
                                <Form.Item
                                    className="full-width-label"
                                    label="New Password"
                                    help={(values.newPassword?.length > 0 && errors.newPassword ? errors.newPassword : null)}
                                    validateStatus={values.newPassword?.length > 0 && errors.newPassword ? 'error' : ''}
                                    required
                                >
                                    <Input.Password
                                        name="newPassword"
                                        value={values.newPassword}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </Form.Item>
                                <Form.Item
                                    className="full-width-label"
                                    label="Confirm New Password"
                                    help={(touched.confirmNewPassword && errors.confirmNewPassword ? errors.confirmNewPassword : null)}
                                    validateStatus={touched.confirmNewPassword && errors.confirmNewPassword ? 'error' : ''}
                                    required
                                >
                                    <Input.Password
                                        name="confirmNewPassword"
                                        value={values.confirmNewPassword}
                                        onChange={(e) => {
                                            const tempNewPass = (values?.newPassword as string).substring(0, e.target.value.length);
                                            if(tempNewPass === e.target.value) 
                                                setFieldTouched("confirmNewPassword", false);
                                            else
                                                setFieldTouched("confirmNewPassword", true);
                                            handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <Form.Item
                                    className="full-width-label"
                                    label="Password Strength"
                                >
                                    <Progress
                                        showInfo={false}
                                        steps={5}
                                        className="password-strength-progress"
                                        percent={(getPasswordStrength() + 1) * 20}
                                        strokeColor={getStrengthColor()}
                                    />
                                </Form.Item>
                                <ul>
                                    <li>10 characters minimum.</li>
                                    <li>15 or more characters is recommended for a stronger password.</li>
                                    <li>Please include at least one upper-case letter, one lower-case letter, one special character, and a number.</li>
                                    <li>Allowed special characters:<br/>!@#$%^*-_+=</li>
                                    <li>Passwords containing common words or patterns are easily hacked.</li>
                                    <li>Avoid using the same password on multiple sites.</li>
                                    <li>Password is case sensitive.</li>
                                </ul>
                            </Col>
                        </Row>
                        <Row justify="end">
                            <Col>
                                <Button
                                    disabled={!canSubmit}
                                    type="primary"
                                    htmlType="submit"
                                    form="PasswordResetForm"
                                >
                                    Reset Password
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                )
            }}
        </Formik>
    </>
}

export default PasswordResetForm